import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    NewspaperIcon,
    ShieldCheckIcon,
    RocketLaunchIcon,
    AcademicCapIcon,
    BeakerIcon,
    ArrowRightIcon,
    ChartBarIcon,
    GlobeAltIcon,
    ClockIcon,
    SignalIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import SEO from '../components/SEO';

// ── Count-up hook ──────────────────────────────────────
const useCountUp = (target, duration = 2000, trigger = false) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (!trigger) return;
        let startTs = null;
        const step = (ts) => {
            if (!startTs) startTs = ts;
            const progress = Math.min((ts - startTs) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(step);
            else setValue(target);
        };
        requestAnimationFrame(step);
    }, [target, duration, trigger]);
    return value;
};

const CountStat = ({ end, label, suffix = '+', icon: Icon, color }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-60px' });
    const count = useCountUp(end, 2000, inView);
    return (
        <motion.div ref={ref} whileHover={{ scale: 1.06 }} className="text-center">
            <Icon className={`h-5 w-5 mx-auto mb-2 ${color}`} />
            <div className="text-2xl md:text-3xl font-black tabular-nums">
                {count.toLocaleString()}{suffix}
            </div>
            <div className="text-xs uppercase tracking-widest opacity-50 font-semibold mt-1">{label}</div>
        </motion.div>
    );
};

// ── Main Component ─────────────────────────────────────
const HomePage = () => {
    const [latestArticles, setLatestArticles] = useState([]);
    const [popularCourses, setPopularCourses]  = useState([]);
    const [tickerNews, setTickerNews]           = useState([]);
    const [loading, setLoading]                 = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchData = async () => {
            try {
                const [articlesRes, coursesRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/articles?pageType=News&limit=6`),
                    axios.get(`${API_BASE_URL}/api/courses?limit=4`),
                ]);
                const articles = Array.isArray(articlesRes.data) ? articlesRes.data : [];
                const courses  = Array.isArray(coursesRes.data)  ? coursesRes.data  : [];
                setLatestArticles(articles.slice(0, 5));
                setPopularCourses(courses.slice(0, 4));
                setTickerNews(articles.map(a => a.title));
            } catch (err) {
                console.error('HomePage fetch error:', err);
                setLatestArticles([]);
                setPopularCourses([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const researchCategories = [
        { icon: RocketLaunchIcon, title: 'Aerodynamics',        description: 'Airflow, lift, drag, and aircraft design principles', gradient: 'from-orange-500 to-red-600',    link: '/research' },
        { icon: BeakerIcon,       title: 'Material Science',    description: 'Advanced composites, alloys and structural materials', gradient: 'from-cyan-500 to-blue-600',    link: '/research' },
        { icon: ChartBarIcon,     title: 'Performance Analysis',description: 'Flight data analysis and efficiency optimisation',    gradient: 'from-violet-500 to-purple-600',link: '/research' },
        { icon: GlobeAltIcon,     title: 'Space Technology',    description: 'Satellite systems, orbital mechanics, and propulsion', gradient: 'from-emerald-500 to-teal-600', link: '/research' },
    ];

    const featuredArticle   = latestArticles[0]   || null;
    const sideArticles      = latestArticles.slice(1, 3);
    const remainingArticles = latestArticles.slice(3, 5);

    return (
        <>
            <SEO
                title="AircoreDynamics — Aviation & Aerospace News, Research & Courses"
                description="Breaking aviation news, in-depth aerospace research, and professional online courses. Stay ahead in aviation and space technology with AircoreDynamics."
                keywords="aviation news, aerospace news, space technology, aerodynamics, aircraft systems, aviation courses, aviation research, flight technology"
                image="https://aircoredynamics.com/og-image.png"
                url="/"
                type="website"
            />

            <div className="relative overflow-x-hidden">

                {/* ════════════════════════════════════════
                    BREAKING NEWS TICKER
                ════════════════════════════════════════ */}
                {tickerNews.length > 0 && (
                    <div className="bg-light-primary dark:bg-dark-primary text-white overflow-hidden py-2 relative z-20 select-none">
                        <div className="flex items-stretch">
                            {/* Label */}
                            <div className="flex-shrink-0 flex items-center gap-2 px-4 bg-black/20 py-1 mr-0">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                                </span>
                                <span className="text-xs font-black uppercase tracking-widest whitespace-nowrap">Breaking</span>
                            </div>
                            {/* Scrolling text */}
                            <div className="overflow-hidden flex-1 ml-4">
                                <div className="animate-ticker">
                                    {[...tickerNews, ...tickerNews].map((title, i) => (
                                        <span key={i} className="text-xs font-medium opacity-90 whitespace-nowrap flex items-center gap-3">
                                            <span className="opacity-40 text-base leading-none">◆</span>
                                            {title}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ════════════════════════════════════════
                    HERO
                ════════════════════════════════════════ */}
                <section className="container mx-auto px-6 pt-12 pb-16 md:pt-16 md:pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">

                        {/* ── Left: Masthead + CTA + Stats ── */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.7, ease: 'easeOut' }}
                            className="lg:col-span-7 order-2 lg:order-1"
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <span className="h-px w-10 bg-light-primary dark:bg-dark-primary" />
                                <span className="text-xs font-black uppercase tracking-widest text-light-primary dark:text-dark-primary">
                                    Aviation & Space Intelligence
                                </span>
                            </div>

                            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-black leading-[1.08] mb-6 tracking-tight">
                                Your Source for{' '}
                                <span className="gradient-text">Aviation</span>
                                {' '}&amp; Aerospace{' '}
                                <span className="relative inline-block">
                                    Intelligence
                                    <motion.span
                                        initial={{ scaleX: 0 }}
                                        animate={{ scaleX: 1 }}
                                        transition={{ delay: 0.9, duration: 0.5, ease: 'easeOut' }}
                                        style={{ originX: 0 }}
                                        className="absolute bottom-0 left-0 right-0 h-[3px] bg-light-primary dark:bg-dark-primary rounded-full"
                                    />
                                </span>
                            </h1>

                            <p className="text-base md:text-lg text-light-text/65 dark:text-dark-text/65 max-w-xl mb-8 leading-relaxed">
                                Breaking news, deep-dive research, and professional courses —
                                everything you need to stay ahead in aviation and space technology.
                            </p>

                            <div className="flex flex-wrap gap-3 mb-10">
                                <Link to="/news" className="btn-primary !px-7 !py-3 !text-sm group inline-flex items-center gap-2">
                                    <NewspaperIcon className="h-4 w-4" />
                                    Latest News
                                    <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                                <Link to="/courses" className="btn-secondary !px-7 !py-3 !text-sm inline-flex items-center gap-2">
                                    <AcademicCapIcon className="h-4 w-4" />
                                    Browse Courses
                                </Link>
                            </div>

                            {/* Stats row */}
                            <div className="grid grid-cols-4 gap-4 pt-6 border-t border-light-border dark:border-dark-border">
                                <CountStat end={50000} suffix="+" label="Followers"    icon={SignalIcon}       color="text-light-primary dark:text-dark-primary" />
                                <CountStat end={1}    suffix="+" label="Courses"      icon={AcademicCapIcon}  color="text-purple-500 dark:text-purple-400" />
                                <CountStat end={200}   suffix="+" label="Articles"     icon={NewspaperIcon}    color="text-pink-500 dark:text-pink-400" />
                                <CountStat end={200}  suffix="+" label="Certificates" icon={ShieldCheckIcon}  color="text-emerald-500 dark:text-emerald-400" />
                            </div>
                        </motion.div>

                        {/* ── Right: Featured Article Hero Card ── */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
                            className="lg:col-span-5 order-1 lg:order-2"
                        >
                            {loading ? (
                                <div className="h-72 md:h-80 rounded-2xl bg-light-card dark:bg-dark-card animate-pulse" />
                            ) : featuredArticle ? (
                                <Link to={`/articles/${featuredArticle.slug || featuredArticle._id}`}>
                                    <motion.div
                                        whileHover={{ y: -6 }}
                                        transition={{ type: 'spring', stiffness: 280, damping: 20 }}
                                        className="relative rounded-2xl overflow-hidden shadow-2xl group"
                                    >
                                        <div className="h-72 md:h-80 bg-gradient-to-br from-light-primary to-blue-600 dark:from-dark-primary dark:to-cyan-700 relative">
                                            {featuredArticle.coverImageUrl && (
                                                <img
                                                    src={featuredArticle.coverImageUrl}
                                                    alt={featuredArticle.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                    referrerPolicy="no-referrer"
                                                    onError={e => e.target.style.display = 'none'}
                                                />
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                                            <div className="absolute top-4 left-4">
                                                <span className="bg-light-primary dark:bg-dark-primary text-white dark:text-dark-bg text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full">
                                                    Featured
                                                </span>
                                            </div>
                                            <div className="absolute bottom-0 left-0 right-0 p-5">
                                                <p className="text-white/60 text-xs mb-2 flex items-center gap-1.5">
                                                    <ClockIcon className="h-3.5 w-3.5" />
                                                    {new Date(featuredArticle.createdAt).toLocaleDateString('en-US', {
                                                        month: 'long', day: 'numeric', year: 'numeric',
                                                    })}
                                                </p>
                                                <h2 className="text-white font-black text-lg leading-snug line-clamp-2 group-hover:text-light-primary dark:group-hover:text-dark-primary transition-colors">
                                                    {featuredArticle.title}
                                                </h2>
                                            </div>
                                        </div>
                                    </motion.div>
                                </Link>
                            ) : null}
                        </motion.div>
                    </div>
                </section>

                {/* ════════════════════════════════════════
                    LATEST NEWS GRID
                ════════════════════════════════════════ */}
                <section className="bg-light-card/40 dark:bg-dark-card/40 border-y border-light-border dark:border-dark-border py-14">
                    <div className="container mx-auto px-6">

                        <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-4">
                                <span className="w-1 h-8 bg-light-primary dark:bg-dark-primary rounded-full" />
                                <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight">Latest News</h2>
                            </div>
                            <Link to="/news" className="text-sm font-bold text-light-primary dark:text-dark-primary hover:underline flex items-center gap-1 group">
                                All News <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>

                        {loading ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="h-64 rounded-xl bg-light-card dark:bg-dark-card animate-pulse" />
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                                {/* Big featured card */}
                                {featuredArticle && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        className="lg:col-span-7"
                                    >
                                        <Link to={`/articles/${featuredArticle.slug || featuredArticle._id}`}>
                                            <motion.div whileHover={{ y: -4 }} className="card overflow-hidden group h-full flex flex-col">
                                                <div className="h-56 bg-gradient-to-br from-light-primary to-blue-600 relative overflow-hidden flex-shrink-0">
                                                    {featuredArticle.coverImageUrl && (
                                                        <img
                                                            src={featuredArticle.coverImageUrl}
                                                            alt={featuredArticle.title}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                            referrerPolicy="no-referrer"
                                                            onError={e => e.target.style.display = 'none'}
                                                        />
                                                    )}
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                                                    <span className="absolute top-3 left-3 bg-light-primary dark:bg-dark-primary text-white dark:text-dark-bg text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                                                        {featuredArticle.category || 'News'}
                                                    </span>
                                                </div>
                                                <div className="p-5 md:p-6 flex flex-col flex-grow">
                                                    <p className="text-xs opacity-50 mb-2 flex items-center gap-1">
                                                        <ClockIcon className="h-3.5 w-3.5" />
                                                        {new Date(featuredArticle.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                    </p>
                                                    <h3 className="text-xl font-black mb-2 line-clamp-2 group-hover:text-light-primary dark:group-hover:text-dark-primary transition-colors leading-snug flex-grow">
                                                        {featuredArticle.title}
                                                    </h3>
                                                    <p className="text-sm opacity-60 line-clamp-2 mb-4">{featuredArticle.excerpt}</p>
                                                    <span className="text-light-primary dark:text-dark-primary text-sm font-bold flex items-center gap-1 mt-auto">
                                                        Read Article
                                                        <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                                    </span>
                                                </div>
                                            </motion.div>
                                        </Link>
                                    </motion.div>
                                )}

                                {/* Side column */}
                                <div className="lg:col-span-5 flex flex-col gap-4">
                                    {/* Horizontal thumb cards */}
                                    {sideArticles.map((article, i) => (
                                        <motion.div
                                            key={article._id}
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: i * 0.1 }}
                                        >
                                            <Link to={`/articles/${article.slug || article._id}`}>
                                                <motion.div whileHover={{ x: 3 }} className="card overflow-hidden group flex">
                                                    <div className="w-28 flex-shrink-0 bg-gradient-to-br from-light-primary/80 to-blue-600/80 relative overflow-hidden">
                                                        {article.coverImageUrl && (
                                                            <img
                                                                src={article.coverImageUrl}
                                                                alt={article.title}
                                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 absolute inset-0"
                                                                referrerPolicy="no-referrer"
                                                                onError={e => e.target.style.display = 'none'}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="p-4 flex-1 min-w-0">
                                                        <span className="text-xs font-bold text-light-primary dark:text-dark-primary uppercase tracking-wide">
                                                            {article.category || 'News'}
                                                        </span>
                                                        <h4 className="font-black text-sm mt-0.5 mb-1 line-clamp-2 group-hover:text-light-primary dark:group-hover:text-dark-primary transition-colors leading-snug">
                                                            {article.title}
                                                        </h4>
                                                        <p className="text-xs opacity-50 flex items-center gap-1">
                                                            <ClockIcon className="h-3 w-3" />
                                                            {new Date(article.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                        </p>
                                                    </div>
                                                </motion.div>
                                            </Link>
                                        </motion.div>
                                    ))}

                                    {/* Ranked minimal list */}
                                    {remainingArticles.map((article, i) => (
                                        <Link key={article._id} to={`/articles/${article.slug || article._id}`}>
                                            <motion.div
                                                whileHover={{ x: 4 }}
                                                className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-light-card dark:hover:bg-dark-card transition-colors group"
                                            >
                                                <span className="text-2xl font-black text-light-primary/20 dark:text-dark-primary/20 w-6 text-right flex-shrink-0 leading-none pt-0.5">
                                                    {i + 3}
                                                </span>
                                                <div className="min-w-0">
                                                    <span className="text-xs font-bold text-light-primary dark:text-dark-primary uppercase tracking-wide">
                                                        {article.category || 'News'}
                                                    </span>
                                                    <h4 className="text-sm font-bold line-clamp-2 group-hover:text-light-primary dark:group-hover:text-dark-primary transition-colors mt-0.5 leading-snug">
                                                        {article.title}
                                                    </h4>
                                                </div>
                                            </motion.div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* ════════════════════════════════════════
                    RESEARCH AREAS
                ════════════════════════════════════════ */}
                <section className="container mx-auto px-6 py-14">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <span className="w-1 h-8 bg-light-primary dark:bg-dark-primary rounded-full" />
                            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight">Research Areas</h2>
                        </div>
                        <Link to="/research" className="text-sm font-bold text-light-primary dark:text-dark-primary hover:underline flex items-center gap-1 group">
                            View All <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {researchCategories.map((cat, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08 }}
                                whileHover={{ y: -6 }}
                            >
                                <Link to={cat.link} className="block h-full">
                                    <div className="card p-6 h-full group relative overflow-hidden">
                                        <div className={`absolute inset-0 bg-gradient-to-br ${cat.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                                        <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${cat.gradient} mb-4`}>
                                            <cat.icon className="h-6 w-6 text-white" strokeWidth={2} />
                                        </div>
                                        <h3 className="font-black text-lg mb-1">{cat.title}</h3>
                                        <p className="text-xs opacity-60 leading-relaxed mb-4">{cat.description}</p>
                                        <div className="flex items-center gap-1 text-xs font-bold text-light-primary dark:text-dark-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                            Explore <ArrowRightIcon className="h-3.5 w-3.5" />
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* ════════════════════════════════════════
                    COURSES
                ════════════════════════════════════════ */}
                <section className="bg-light-card/40 dark:bg-dark-card/40 border-y border-light-border dark:border-dark-border py-14">
                    <div className="container mx-auto px-6">
                        <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-4">
                                <span className="w-1 h-8 bg-light-primary dark:bg-dark-primary rounded-full" />
                                <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight">Popular Courses</h2>
                            </div>
                            <Link to="/courses" className="text-sm font-bold text-light-primary dark:text-dark-primary hover:underline flex items-center gap-1 group">
                                All Courses <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>

                        {loading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="h-52 rounded-xl bg-light-card dark:bg-dark-card animate-pulse" />
                                ))}
                            </div>
                        ) : popularCourses.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                                {popularCourses.map((course, i) => (
                                    <motion.div
                                        key={course._id}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.08 }}
                                        whileHover={{ y: -6 }}
                                    >
                                        <Link to={`/courses/${course.slug || course._id}`} className="block h-full">
                                            <div className="card overflow-hidden group h-full flex flex-col">
                                                <div className="h-32 bg-gradient-to-br from-light-primary to-blue-600 dark:from-dark-primary dark:to-cyan-700 flex items-center justify-center relative overflow-hidden flex-shrink-0">
                                                    {course.coverImage ? (
                                                        <img
                                                            src={course.coverImage}
                                                            alt={course.title}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 absolute inset-0"
                                                            referrerPolicy="no-referrer"
                                                            onError={e => e.target.style.display = 'none'}
                                                        />
                                                    ) : null}
                                                    <AcademicCapIcon className="h-12 w-12 text-white/25 relative z-10" />
                                                    {course.level && (
                                                        <span className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full font-semibold z-10">
                                                            {course.level}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="p-4 flex flex-col flex-grow">
                                                    <h3 className="font-black text-sm line-clamp-2 mb-2 group-hover:text-light-primary dark:group-hover:text-dark-primary transition-colors flex-grow">
                                                        {course.title}
                                                    </h3>
                                                    <p className="text-xs opacity-50 line-clamp-2 mb-3">{course.description}</p>
                                                    <span className="text-xs font-bold text-light-primary dark:text-dark-primary flex items-center gap-1 mt-auto">
                                                        Start Course
                                                        <ArrowRightIcon className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                                                    </span>
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </section>

                {/* ════════════════════════════════════════
                    CTA
                ════════════════════════════════════════ */}
                <section className="container mx-auto px-6 py-14">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="relative overflow-hidden rounded-2xl border-2 border-light-primary dark:border-dark-primary p-10 md:p-16 text-center"
                    >
                        {/* Subtle animated blobs */}
                        <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
                            <motion.div
                                animate={{ scale: [1, 1.4, 1], rotate: [0, 180, 0] }}
                                transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
                                className="absolute -top-24 -right-24 w-72 h-72 bg-light-primary/5 dark:bg-dark-primary/5 rounded-full blur-3xl"
                            />
                            <motion.div
                                animate={{ scale: [1, 1.3, 1], rotate: [0, -180, 0] }}
                                transition={{ duration: 17, repeat: Infinity, ease: 'linear' }}
                                className="absolute -bottom-24 -left-24 w-80 h-80 bg-light-primary/5 dark:bg-dark-primary/5 rounded-full blur-3xl"
                            />
                        </div>

                        <div className="relative z-10">
                            <RocketLaunchIcon className="h-10 w-10 mx-auto mb-5 text-light-primary dark:text-dark-primary" />
                            <h2 className="text-3xl md:text-4xl font-black mb-4 tracking-tight">
                                Ready to Start Your Journey?
                            </h2>
                            <p className="text-base md:text-lg opacity-65 mb-8 max-w-xl mx-auto leading-relaxed">
                                Join thousands of aviation enthusiasts, students, and professionals
                                learning on AircoreDynamics.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Link to="/register" className="btn-primary !px-8 !py-3.5 !text-base">
                                    Create Free Account
                                </Link>
                                <Link to="/courses" className="btn-secondary !px-8 !py-3.5 !text-base">
                                    Explore Courses
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                </section>

            </div>
        </>
    );
};

export default HomePage;